import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '@/i18n';
import { monthlyCutsService } from '@/api/domains/attendance';
import type { MonthlyCutDetail, MonthlyCutGroup } from '@/api/domains/attendance';
import { useUI } from '@/hooks/ui/useUI';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';


// Tamaño de página al recorrer las agrupaciones asignadas. El detalle solo lee, así que se pagina
// hasta agotar el total en vez de imponer un tope silencioso a lo que se muestra.
const GROUPS_PAGE_SIZE = 100;

export interface UseMonthlyCutDetailOptions {
  monthlyCutId: number;
}

export interface UseMonthlyCutDetailResult {
  detail: MonthlyCutDetail | null;
  detailErrorMessage: string | null;
  assignedGroups: MonthlyCutGroup[];
  // Distingue "todavía cargando" de "cargó y no hay agrupaciones", independiente del resultado del
  // detalle: ambas peticiones viajan en paralelo y una puede fallar mientras la otra tiene éxito.
  groupsLoaded: boolean;
}

/**
 * Carga los datos de solo lectura de un corte mensual para el panel de detalle (ASIS-4964).
 *
 * Combina dos fuentes: el detalle del corte (que aporta `applyToAllCompanies`, dato que el listado
 * no expone) y las agrupaciones asignadas con su nombre — `detail.groupIds` solo trae los
 * identificadores, que no son presentables al usuario.
 */
export function useMonthlyCutDetail(
  options: UseMonthlyCutDetailOptions,
): UseMonthlyCutDetailResult {
  const { monthlyCutId } = options;
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  // El spinner lo pinta el contenedor del panel, no el contenido (patrón de los demás paneles de
  // detalle: useAttendanceConceptDetailPanel, etc.).
  const { setLoading } = useSidePanelLoading();

  const [detail, setDetail] = useState<MonthlyCutDetail | null>(null);
  const [detailErrorMessage, setDetailErrorMessage] = useState<string | null>(null);
  const [assignedGroups, setAssignedGroups] = useState<MonthlyCutGroup[]>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  // Recorre todas las páginas de agrupaciones asignadas. Devuelve null si la petición se abortó o
  // falló, para que el caller no sobrescriba el estado con datos parciales.
  const loadAssignedGroups = useCallback(
    async (signal: AbortSignal): Promise<MonthlyCutGroup[] | null> => {
      const accumulated: MonthlyCutGroup[] = [];
      let page = 1;
      let totalRecords = Number.POSITIVE_INFINITY;

      while (accumulated.length < totalRecords) {
        const response = await monthlyCutsService.getAssignedGroups(
          { monthlyCutId, page, pageSize: GROUPS_PAGE_SIZE, orderDirection: 'ASC' },
          signal,
        );

        if (response.error?.code === 'ABORTED') return null;
        if (!response.success || !response.data) {
          showApiError(response, translate('attendance:monthlyCutDetail.messages.groupsLoadError'));
          return null;
        }

        accumulated.push(...response.data.items);
        totalRecords = response.data.totalRecords;
        if (response.data.items.length === 0) break;
        page += 1;
      }

      return accumulated;
    },
    [monthlyCutId, showApiError, translate],
  );

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setDetail(null);
      setDetailErrorMessage(null);
      setAssignedGroups([]);
      setGroupsLoaded(false);

      try {
        const [detailResponse, groups] = await Promise.all([
          monthlyCutsService.getDetail(monthlyCutId, controller.signal),
          loadAssignedGroups(controller.signal),
        ]);

        if (controller.signal.aborted) return;

        if (detailResponse.error?.code !== 'ABORTED') {
          if (!detailResponse.success || !detailResponse.data) {
            const message = showApiError(
              detailResponse,
              translate('attendance:monthlyCutDetail.messages.loadError'),
            );
            setDetailErrorMessage(message ?? translate('attendance:monthlyCutDetail.messages.loadError'));
          } else {
            setDetail(detailResponse.data);
          }
        }

        if (groups !== null) setAssignedGroups(groups);
        setGroupsLoaded(true);
      } finally {
        // Si se abortó (unmount o un `monthlyCutId` nuevo que ya disparó su propio `load()`), no
        // reportamos fin de carga: haríamos que el contenedor oculte el spinner de la petición
        // vigente, no de esta.
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    void load();
    return () => { controller.abort(); };
  }, [monthlyCutId, loadAssignedGroups, setLoading, showApiError, translate]);

  return { detail, detailErrorMessage, assignedGroups, groupsLoaded };
}

export default useMonthlyCutDetail;
